import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'siteFinancials'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

const initialState = {
	bySite: {} as Record<string, Record<string, any>>, // bySite[siteId][modelType] = model
	comparisonByEvaluation: {} as Record<string, any[]>,
	// metricsBySite[siteId][modelType] = { rollup, isCalculating }
	// Populated by the calculateMetrics thunk hitting B2's endpoint. Stale while
	// a request is in flight; rollup stays at the last successful value so the
	// UI doesn't flash empty between debounced keystrokes.
	metricsBySite: {} as Record<
		string,
		Record<string, { rollup: any; isCalculating: boolean }>
	>,
	isLoading: false,
	error: null as string | null,
}

export const fetchSiteFinancials = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (
		{ evalId, siteId }: { evalId: string; siteId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/sites/${siteId}/financials`,
				prepConfig(getState),
			)
			return { siteId, models: data }
		} catch {
			return rejectWithValue('Failed to load site financials')
		}
	},
)

export const upsertFinancialModel = createAsyncThunk(
	`${SLICE_NAME}/upsert`,
	async (
		{
			evalId,
			siteId,
			modelType,
			lineItems,
			assumptions,
		}: {
			evalId: string
			siteId: string
			modelType: string
			lineItems: any
			assumptions?: any
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.put(
				`/api/evaluations/${evalId}/sites/${siteId}/financials`,
				{ modelType, lineItems, assumptions },
				prepConfig(getState),
			)
			return { siteId, model: data }
		} catch {
			return rejectWithValue('Failed to save financial model')
		}
	},
)

export const calculateMetrics = createAsyncThunk(
	`${SLICE_NAME}/calculate`,
	async (
		{
			evalId,
			siteId,
			modelType,
			lineItems,
			assumptions,
		}: {
			evalId: string
			siteId: string
			modelType: string
			lineItems: any
			assumptions?: any
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/sites/${siteId}/financials/${modelType}/calculate`,
				{ lineItems, assumptions },
				prepConfig(getState),
			)
			return { siteId, modelType, rollup: data }
		} catch {
			return rejectWithValue('Failed to calculate metrics')
		}
	},
)

export const fetchFinancialsComparison = createAsyncThunk(
	`${SLICE_NAME}/fetchCompare`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/financials/compare`,
				prepConfig(getState),
			)
			return { evalId, comparison: data }
		} catch {
			return rejectWithValue('Failed to load financials comparison')
		}
	},
)

const siteFinancialSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSiteFinancials.fulfilled, (state, action) => {
				const { siteId, models } = action.payload
				state.bySite[siteId] = {}
				for (const model of models) {
					state.bySite[siteId][model.modelType] = model
				}
			})
			.addCase(upsertFinancialModel.fulfilled, (state, action) => {
				const { siteId, model } = action.payload
				if (!state.bySite[siteId]) state.bySite[siteId] = {}
				state.bySite[siteId][model.modelType] = model
			})
			.addCase(fetchFinancialsComparison.fulfilled, (state, action) => {
				const { evalId, comparison } = action.payload
				state.comparisonByEvaluation[evalId] = comparison
			})
			.addCase(calculateMetrics.pending, (state, action) => {
				const { siteId, modelType } = action.meta.arg
				if (!state.metricsBySite[siteId]) state.metricsBySite[siteId] = {}
				const prev = state.metricsBySite[siteId][modelType]
				state.metricsBySite[siteId][modelType] = {
					rollup: prev?.rollup ?? null,
					isCalculating: true,
				}
			})
			.addCase(calculateMetrics.fulfilled, (state, action) => {
				const { siteId, modelType, rollup } = action.payload
				if (!state.metricsBySite[siteId]) state.metricsBySite[siteId] = {}
				state.metricsBySite[siteId][modelType] = { rollup, isCalculating: false }
			})
			.addCase(calculateMetrics.rejected, (state, action) => {
				const { siteId, modelType } = action.meta.arg
				if (!state.metricsBySite[siteId]) state.metricsBySite[siteId] = {}
				const prev = state.metricsBySite[siteId][modelType]
				state.metricsBySite[siteId][modelType] = {
					rollup: prev?.rollup ?? null,
					isCalculating: false,
				}
			})
	},
})

// ============================================================================
// Apply a financial template's line items + assumptions across the
// evaluation's sites.
//
// Mirrors POST /api/evaluations/:evalId/apply-financial-template. The shared
// `ApplyTemplateModalCN` calls this URL directly via axios — this thunk is a
// Redux-shaped affordance for callers that prefer dispatching (state
// inspection in devtools, error in slice state, etc.).
//
// Returns the server's { templateId, templateName, modelType, sites: {
// applied, replaced, skipped } } payload. Callers should re-fetch financials
// + the comparison to refresh derived state.
// ============================================================================
export const applyFinancialTemplate = createAsyncThunk(
	`${SLICE_NAME}/applyTemplate`,
	async (
		payload: {
			evalId: string
			templateId: string
			modelType: 'acquisition' | 'operating' | 'roi'
			overwrite?: boolean
			applyTo?: 'all' | string
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const { evalId, templateId, modelType, overwrite, applyTo } = payload
			const { data } = await axios.post(
				`/api/evaluations/${evalId}/apply-financial-template`,
				{
					templateId,
					modelType,
					overwrite: !!overwrite,
					applyTo: applyTo || 'all',
				},
				prepConfig(getState),
			)
			return data
		} catch (err: any) {
			return rejectWithValue(
				err?.response?.data?.message || 'Failed to apply financial template',
			)
		}
	},
)

export default siteFinancialSlice
